import { Injector, LOCALE_ID } from '@angular/core';
import { Validators } from '@angular/forms';
import { BodyClasses, ColumnSizes, FieldSelectOption, FilterType } from 'components';
import { DateDisplayPipe } from 'projects/components/src/lib/shared/pipes/date-display/date-display.pipe';
import { Report } from '../../models/report';
import { LocationsLookupService, AttendingPhysiciansLookupService } from '../../../../core/services/lookup';
import { DailyActivitySummaryService } from './daily-activity-summary.service';

export const DailyActivitySummary: Report = {
  id: 'daily-activity-summary',
  label: 'Daily Activity Summary',
  textCriteria: {
    'Start Date': '', 'End Date': '', 'Locations': '', 'Attending Provider': '', 'Report Format': ''
  },
  cleanFilterParams: {
    'locationIds': '', 'attendingProviderIds': ''
  },
  getReportTitle: () => {
    return DailyActivitySummary.label + ' ' + DailyActivitySummary.textCriteria['Start Date'] + ' - ' + DailyActivitySummary.textCriteria['End Date']
      + ' (' + DailyActivitySummary.textCriteria['Report Format'] + ')';
  },
  getSearchCriteria: (injector: Injector) => [
    {
      title: '',
      controls: [
        {
          label: 'Posting Date Range',
          name: 'dateRange',
          type: 'dateRange',
          class: 'form-span-12',
          daterangeStartChanged: (event) => {
            DailyActivitySummary.textCriteria['Start Date'] = event.value.toLocaleDateString();
          },
          daterangeEndChanged: (event) => {
            DailyActivitySummary.textCriteria['End Date'] = event.value.toLocaleDateString();
          }
        },
        {
          label: '',
          name: 'dateRangeStart',
          type: 'noInput',
          class: 'display-none',
          validators: Validators.required
        },
        {
          label: '',
          name: 'dateRangeEnd',
          type: 'noInput',
          class: 'display-none',
          validators: Validators.required
        },
        {
          label: 'Report Format',
          name: 'reportFormat',
          class: 'form-span-12',
          type: 'select',
          options: [
            {
              label: 'Sorted by date showing activity by provider',
              value: '1'
            },
            {
              label: 'Sorted by month showing activity by provider',
              value: '2'
            },
            {
              label: 'Sorted by location of service',
              value: '3'
            }
          ],
          initial: '3',
          validators: Validators.required,
          selectionChanged: (event) => {
            DailyActivitySummary.textCriteria['Report Format'] = event.source.triggerValue;
          }
        },
        {
          matLabel: 'Location',
          name: 'locationIds',
          apiService: injector.get(LocationsLookupService),
          type: 'multiSelect',
          class: 'form-span-12 multi-input',
          placeholder: 'Location',
          selectOptions: (event) => {
            let listValue = []
            let listText = []
            event.forEach((x: FieldSelectOption) => {

              listValue.push(x.value)
              listText.push(x.label)
            })
            DailyActivitySummary.textCriteria['Locations'] = listText.join(',');
            DailyActivitySummary.cleanFilterParams['locationIds'] = listValue
          }
        },
        {
          matLabel: 'Attending Provider',
          name: 'attendingProviderIds',
          apiService: injector.get(AttendingPhysiciansLookupService),
          type: 'multiSelect',
          class: 'form-span-12 multi-input',
          placeholder: 'Attending Provider',
          selectOptions: (event) => {
            let listValue = []
            let listText = []
            event.forEach((x: FieldSelectOption) => {
              listValue.push(x.value)
              listText.push(x.label)
            })
            DailyActivitySummary.textCriteria['Attending Providers'] = listText.join(',');
            DailyActivitySummary.cleanFilterParams['attendingProviderIds'] = listValue

          }
        },
        {
          label: 'Exclude Refunds',
          name: 'excludeRefunds',
          class: 'form-span-12',
          type: 'toggle',
          initial: false,
          toggleChanged: (event) => {
            DailyActivitySummary.textCriteria['Exclude Refunds'] = event.checked === true ? 'Yes' : 'No'
          }
        },
      ]
    }
  ],
  getGridConfiguration: (injector: Injector) => {
    const locale = injector.get(LOCALE_ID);
    const datePipe: DateDisplayPipe = new DateDisplayPipe(locale);
    const moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    return {
      service: injector.get<any>(DailyActivitySummaryService),
      columnDefinitions: [
        {
          id: 'Posting Month',
          displayKey: 'Posting Month',
          headerText: 'Posting Month',
          displayOrder: 0,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true
        },
        {
          id: 'Posting Date',
          displayKey: 'Posting Date',
          headerText: 'Posting Date',
          displayOrder: 1,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true,
          sortColumnType: 'date',
          filterConfig: {
            filterType: FilterType.date
          }
        },
        {
          id: 'Location',
          displayKey: 'Location',
          headerText: 'Location',
          displayOrder: 2,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true
        },
        {
          id: 'Location Name',
          displayKey: 'Location Name',
          headerText: 'Location Name',
          displayOrder: 3,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true
        },
        {
          id: 'Provider',
          displayKey: 'Provider',
          headerText: 'Provider',
          displayOrder: 4,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true
        },
        {
          id: 'Provider Name',
          displayKey: 'Provider Name',
          headerText: 'Provider Name',
          displayOrder: 5,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true
        },
        {
          id: 'Procedure Amount',
          displayKey: 'Procedure Amount',
          headerText: 'Procedure Amount',
          displayOrder: 6,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true,
          sortColumnType: 'number',
          filterConfig: {
            filterType: FilterType.number
          },
        },
        {
          id: 'Paid Amount',
          displayKey: 'Paid Amount',
          headerText: 'Paid Amount',
          displayOrder: 7,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true,
          sortColumnType: 'number',
          filterConfig: {
            filterType: FilterType.number
          },
        },
        {
          id: 'Adjustment Amount',
          displayKey: 'Adjustment Amount',
          headerText: 'Adjustment Amount',
          displayOrder: 8,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true,
          sortColumnType: 'number',
          filterConfig: {
            filterType: FilterType.number
          },
        },
        {
          id: 'Net',
          displayKey: 'Net',
          headerText: 'Net',
          displayOrder: 9,
          columnSize: ColumnSizes.normal,
          filterable: true,
          sortable: true,
          sortColumnType: 'number',
          filterConfig: {
            filterType: FilterType.number
          },
        }
      ]
    };
  }
};

